/**
 * Les logos imprimés en tête des documents de l'entreprise (bon d'intervention, puis devis et factures).
 *
 * Un logo principal (celui de l'entreprise) et jusqu'à trois logos de partenaires ou de certifications. Chaque dépôt passe
 * par le glisser-déposer (convention n° 7), est REDUIT ET RECODÉ dans le navigateur (`preparerLogo` : un bon pèse moins de
 * 150 Ko, règle non négociable de Benjamin), stocké comme un fichier de l'espace, puis désigné dans les paramètres.
 *
 * Les gestes de cette carte s'appliquent TOUT DE SUITE (déposer, retirer, déplacer) : un logo n'attend pas le bouton
 * « Enregistrer » du formulaire, qui ne contient que du texte. Une erreur s'affiche dans la carte, là où l'on a cliqué.
 */
import { useState } from 'react';

import { ApiError, api, type Settings } from '../api/client';
import { signalerIdentiteModifiee, useIdentiteEspace } from '../domaine/identite-espace';
import {
  FORMATS_LOGO,
  LARGEUR_LOGO_PRINCIPAL,
  LARGEUR_LOGO_SECONDAIRE,
  PLAFOND_LOGOS_SECONDAIRES,
  poidsLisible,
  preparerLogo,
  refusDuFichierLogo,
  type RoleLogo,
} from '../domaine/logos';
import { useUrlsFichiers } from '../domaine/urls-fichiers';
import { ZoneDepot } from './ZoneDepot';

export function LogosEntreprise({
  reglages,
  onChange,
}: {
  reglages: Settings;
  /** Les réglages relus après un geste : le parent garde son formulaire de texte tel quel. */
  onChange: (reglages: Settings) => void;
}) {
  const identite = useIdentiteEspace();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const principal = reglages.logoFileId;
  const secondaires = reglages.secondaryLogoFileIds;
  const { urls } = useUrlsFichiers([...(principal ? [principal] : []), ...secondaires]);

  const appliquer = async (corps: Record<string, unknown>, message: string) => {
    const resultat = await api.updateSettings(corps);
    onChange(resultat);
    // Le menu et les documents ouverts relisent l'identité de l'espace.
    signalerIdentiteModifiee();
    setSucces(message);
  };

  const echec = (e: unknown, defaut: string) => setErreur(e instanceof ApiError || e instanceof Error ? e.message : defaut);

  const deposer = async (fichiers: readonly File[], role: RoleLogo) => {
    const fichier = fichiers[0];
    if (!fichier) return;
    setErreur(null);
    setSucces(null);
    const refus = refusDuFichierLogo(fichier);
    if (refus) {
      setErreur(refus);
      return;
    }
    const espaceId = identite?.espaceId;
    if (!espaceId) {
      setErreur('L’espace n’a pas pu être identifié : rechargez la page.');
      return;
    }
    setEnCours(true);
    try {
      const prepare = await preparerLogo(fichier, role, secondaires.length + 1);
      const piece = await api.uploadFile('tenant', espaceId, prepare);
      if (role === 'principal') {
        await appliquer({ logoFileId: piece.id }, `Logo enregistré (${poidsLisible(piece.byteSize)}).`);
      } else {
        await appliquer(
          { secondaryLogoFileIds: [...secondaires, piece.id] },
          `Logo ajouté (${poidsLisible(piece.byteSize)}).`,
        );
      }
    } catch (e) {
      echec(e, 'Le logo n’a pas pu être enregistré.');
    } finally {
      setEnCours(false);
    }
  };

  const retirerPrincipal = async () => {
    if (!principal) return;
    setErreur(null);
    setSucces(null);
    setEnCours(true);
    try {
      await appliquer({ logoFileId: null }, 'Logo retiré.');
      // Le fichier est archivé ensuite (jamais supprimé) : s'il ne l'est pas, il ne sert plus à rien, sans conséquence.
      await api.archiveFile(principal).catch(() => undefined);
    } catch (e) {
      echec(e, 'Le logo n’a pas pu être retiré.');
    } finally {
      setEnCours(false);
    }
  };

  const retirerSecondaire = async (id: string) => {
    setErreur(null);
    setSucces(null);
    setEnCours(true);
    try {
      await appliquer({ secondaryLogoFileIds: secondaires.filter((x) => x !== id) }, 'Logo retiré.');
      await api.archiveFile(id).catch(() => undefined);
    } catch (e) {
      echec(e, 'Le logo n’a pas pu être retiré.');
    } finally {
      setEnCours(false);
    }
  };

  const deplacer = async (index: number, sens: -1 | 1) => {
    const cible = index + sens;
    if (cible < 0 || cible >= secondaires.length) return;
    const liste = [...secondaires];
    const a = liste[index];
    const b = liste[cible];
    if (a === undefined || b === undefined) return;
    liste[index] = b;
    liste[cible] = a;
    setErreur(null);
    setSucces(null);
    setEnCours(true);
    try {
      await appliquer({ secondaryLogoFileIds: liste }, 'Ordre des logos enregistré.');
    } catch (e) {
      echec(e, 'L’ordre n’a pas pu être enregistré.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="carte">
      <h2>Logos des documents</h2>
      <div className="carte-corps">
        <p className="aide">
          Ils s’impriment en tête du bon d’intervention. Chaque image est réduite et rognée ici avant l’envoi (un document
          doit rester léger pour partir par courriel) : déposez simplement le fichier d’origine.
        </p>

        {erreur && <div className="message erreur">{erreur}</div>}
        {succes && <div className="message succes">{succes}</div>}

        <div className="champ">
          <label>Logo de l’entreprise</label>
          <div className="logos-ligne">
            {principal && (
              <div className="logo-vignette">
                {urls[principal] ? <img src={urls[principal]} alt="Logo de l’entreprise" /> : <span className="aide">Aperçu indisponible</span>}
                <button type="button" className="secondaire discret" disabled={enCours} onClick={() => void retirerPrincipal()}>
                  Retirer
                </button>
              </div>
            )}
            <ZoneDepot
              compacte
              accept={FORMATS_LOGO}
              desactive={enCours}
              libelle={principal ? 'Glissez un nouveau logo ici pour le remplacer, ou' : 'Glissez le logo ici, ou'}
              boutonLibelle="Choisir un fichier"
              ariaLabel="Déposer le logo de l’entreprise"
              onFichiers={(f) => void deposer(f, 'principal')}
            />
          </div>
          <p className="aide">Réduit à {LARGEUR_LOGO_PRINCIPAL} points de large, en JPEG sur fond blanc.</p>
        </div>

        <div className="champ">
          <label>Logos de partenaires et de certifications</label>
          <div className="logos-ligne">
            {secondaires.map((id, i) => (
              <div className="logo-vignette" key={id}>
                {urls[id] ? <img src={urls[id]} alt={`Logo de partenaire ${i + 1}`} /> : <span className="aide">Aperçu indisponible</span>}
                <div className="saisie-ligne">
                  <button type="button" className="secondaire discret" title="Plus à gauche" disabled={enCours || i === 0} onClick={() => void deplacer(i, -1)}>
                    ←
                  </button>
                  <button
                    type="button"
                    className="secondaire discret"
                    title="Plus à droite"
                    disabled={enCours || i === secondaires.length - 1}
                    onClick={() => void deplacer(i, 1)}
                  >
                    →
                  </button>
                  <button type="button" className="secondaire discret" disabled={enCours} onClick={() => void retirerSecondaire(id)}>
                    Retirer
                  </button>
                </div>
              </div>
            ))}
            {secondaires.length < PLAFOND_LOGOS_SECONDAIRES && (
              <ZoneDepot
                compacte
                accept={FORMATS_LOGO}
                desactive={enCours}
                libelle="Glissez un logo ici, ou"
                boutonLibelle="Choisir un fichier"
                ariaLabel="Déposer un logo de partenaire"
                onFichiers={(f) => void deposer(f, 'secondaire')}
              />
            )}
          </div>
          <p className="aide">
            {PLAFOND_LOGOS_SECONDAIRES} au plus (KAESER, MASE…), réduits à {LARGEUR_LOGO_SECONDAIRE} points de large.
            {secondaires.length >= PLAFOND_LOGOS_SECONDAIRES ? ' Retirez-en un pour en ajouter un autre.' : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
